import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { FaPlay, FaPause, FaGavel, FaHandPaper, FaBan, FaComment, FaPlus } from 'react-icons/fa';
import { useAuctionAdmin } from '../../hooks/useAuctionAdmin';
const formatPrice = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
const SupervisedAdminPanel = ({ auctionId, supervisedState, currentBid, bidCount, onStateChange, }) => {
    const { supervisedAction } = useAuctionAdmin();
    const [isProcessing, setIsProcessing] = useState(false);
    const [manualBidAmount, setManualBidAmount] = useState('');
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [status, setStatus] = useState(null);
    const handleAction = useCallback(async (action, payload) => {
        setIsProcessing(true);
        setStatus(null);
        try {
            const result = await supervisedAction(auctionId, action, payload);
            if (result.success) {
                setStatus({ type: 'success', message: `Action "${action}" completed successfully` });
                onStateChange?.('data' in result && result.data?.newState || '');
            }
            else {
                setStatus({ type: 'error', message: ('error' in result && result.error) || 'Action failed' });
            }
        }
        catch (err) {
            setStatus({ type: 'error', message: err.message });
        }
        finally {
            setIsProcessing(false);
        }
    }, [auctionId, supervisedAction, onStateChange]);
    const handleManualBid = async () => {
        const amount = parseFloat(manualBidAmount);
        if (isNaN(amount) || amount <= 0)
            return;
        await handleAction('manual_bid', { bidAmount: amount });
        setManualBidAmount('');
    };
    const stateLabel = {
        draft: 'Draft',
        accepting_bids: 'Accepting Bids',
        paused: 'Paused',
        going_once: 'Going Once...',
        going_twice: 'Going Twice...',
        sold: 'SOLD!',
        closed: 'Closed',
    };
    const isUrgent = supervisedState === 'going_once' || supervisedState === 'going_twice';
    return (_jsxs(Panel, { children: [_jsx(PanelHeader, { children: _jsxs(PanelTitle, { children: [_jsx(FaGavel, {}), " Auctioneer Controls"] }) }), _jsxs(StateDisplay, { "$urgent": isUrgent, children: [_jsx(StateText, { "$state": supervisedState || 'draft', children: stateLabel[supervisedState || 'draft'] || supervisedState }), _jsxs(StatsMeta, { children: ["Current: ", formatPrice(currentBid), " \u00B7 ", bidCount, " bid", bidCount !== 1 ? 's' : ''] })] }), _jsxs(ButtonGrid, { children: [_jsxs(ControlButton, { "$variant": "primary", onClick: () => handleAction('open_bidding'), disabled: isProcessing || supervisedState === 'accepting_bids', children: [_jsx(FaPlay, {}), " Open Bidding"] }), _jsxs(ControlButton, { onClick: () => handleAction('pause_bidding'), disabled: isProcessing || supervisedState === 'paused', children: [_jsx(FaPause, {}), " Pause"] }), _jsxs(ControlButton, { "$variant": "warning", onClick: () => handleAction('going_once'), disabled: isProcessing || bidCount === 0, children: [_jsx(FaHandPaper, {}), " Going Once"] }), _jsxs(ControlButton, { "$variant": "warning", onClick: () => handleAction('going_twice'), disabled: isProcessing, children: [_jsx(FaHandPaper, {}), " Going Twice"] }), _jsxs(ControlButton, { "$variant": "gold", onClick: () => handleAction('sold'), disabled: isProcessing || bidCount === 0, children: [_jsx(FaGavel, {}), " SOLD!"] }), _jsxs(ControlButton, { "$variant": "danger", onClick: () => handleAction('no_sale'), disabled: isProcessing, children: [_jsx(FaBan, {}), " No Sale"] })] }), _jsx(Divider, {}), _jsxs(SectionLabel, { children: [_jsx(FaPlus, {}), " Manual Bid Entry"] }), _jsxs(BidEntryRow, { children: [_jsx(BidEntryInput, { type: "number", placeholder: "Bid amount...", value: manualBidAmount, onChange: (e) => setManualBidAmount(e.target.value) }), _jsx(ControlButton, { "$variant": "primary", onClick: handleManualBid, disabled: isProcessing || !manualBidAmount, children: "Enter Bid" })] }), _jsx(Divider, {}), _jsxs(SectionLabel, { children: [_jsx(FaComment, {}), " Broadcast Message"] }), _jsx(BidEntryInput, { as: "input", placeholder: "Type a message to all watchers...", value: broadcastMessage, onChange: (e) => setBroadcastMessage(e.target.value), onKeyDown: (e) => {
                    if (e.key === 'Enter' && broadcastMessage.trim()) {
                        setStatus({ type: 'success', message: `Broadcast sent: "${broadcastMessage.trim()}"` });
                        setBroadcastMessage('');
                        setTimeout(() => setStatus(null), 3000);
                    }
                } }), status && (_jsx(FeedbackMessage, { "$type": status.type, children: status.message }))] }));
};
export default SupervisedAdminPanel;
// ── Animations ──────────────────────────────────────────
const pulse = keyframes `
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;
// ── Styled Components (matching AuctionDetail page tokens) ──
const Panel = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #B8860B));
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
const PanelHeader = styled.div `
  margin-bottom: 16px;
`;
const PanelTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: var(--accent-gold, #e6c068);
    width: 18px;
    height: 18px;
  }
`;
const StateDisplay = styled.div `
  text-align: center;
  margin-bottom: 20px;
  padding: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);

  ${props => props.$urgent && css `
    border-color: rgba(234, 179, 8, 0.3);
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}
`;
const stateColorMap = {
    accepting_bids: '#22c55e',
    going_once: '#eab308',
    going_twice: '#f97316',
    paused: '#3b82f6',
    sold: '#e6c068',
    closed: '#6b7280',
    draft: '#9ca3af',
};
const StateText = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 22px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${({ $state }) => stateColorMap[$state] || '#9ca3af'};
  line-height: 1.2;
`;
const StatsMeta = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 6px;
`;
const ButtonGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const variantStyles = {
    primary: css `
    background: rgba(34, 197, 94, 0.08);
    color: #22c55e;
    border-color: rgba(34, 197, 94, 0.25);
    &:hover:not(:disabled) { background: rgba(34, 197, 94, 0.15); }
  `,
    warning: css `
    background: rgba(234, 179, 8, 0.08);
    color: #eab308;
    border-color: rgba(234, 179, 8, 0.25);
    &:hover:not(:disabled) { background: rgba(234, 179, 8, 0.15); }
  `,
    danger: css `
    background: rgba(239, 68, 68, 0.08);
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.25);
    &:hover:not(:disabled) { background: rgba(239, 68, 68, 0.15); }
  `,
    gold: css `
    background: linear-gradient(135deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #B8860B));
    color: #fff;
    border-color: transparent;
    font-weight: 700;
    letter-spacing: 0.5px;
    &:hover:not(:disabled) {
      box-shadow: 0 4px 16px rgba(230, 192, 104, 0.3);
      transform: translateY(-1px);
    }
  `,
};
const defaultBtnStyle = css `
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border-color: var(--border-color);
  &:hover:not(:disabled) { background: var(--bg-tertiary, rgba(255,255,255,0.04)); }
`;
const ControlButton = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  svg {
    width: 12px;
    height: 12px;
  }

  ${props => props.$variant ? variantStyles[props.$variant] : defaultBtnStyle}

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;
const Divider = styled.div `
  height: 1px;
  background: var(--border-color);
  margin: 16px 0;
`;
const SectionLabel = styled.div `
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--text-tertiary);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;

  svg {
    width: 11px;
    height: 11px;
    color: var(--accent-gold, #e6c068);
  }
`;
const BidEntryRow = styled.div `
  display: flex;
  gap: 8px;
  align-items: center;
`;
const BidEntryInput = styled.input `
  flex: 1;
  padding: 12px 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #e6c068);
    box-shadow: 0 0 0 3px rgba(230, 192, 104, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
const FeedbackMessage = styled.div `
  padding: 10px 14px;
  margin-top: 16px;
  font-size: 13px;
  background: ${({ $type }) => $type === 'success' ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)'};
  color: ${({ $type }) => $type === 'success' ? '#22c55e' : '#ef4444'};
  border: 1px solid ${({ $type }) => $type === 'success' ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)'};
`;
